'use client';

import React, { useState } from 'react';

export default function GuestProfile() {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    firstName: 'Sarah',
    lastName: 'Nakato',
    email: 'sarah.nakato@email.com',
    phone: '+256 700 123 456',
    country: 'Uganda',
    city: 'Kampala',
    bio: 'Passionate traveler exploring East Africa one stay at a time. Love nature, wildlife, and authentic local experiences.',
    language: 'English',
    currency: 'UGX',
  });

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="max-w-3xl">
      {/* Profile Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
        <div className="flex items-center gap-5">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center text-white font-bold text-3xl">
              S
            </div>
            <button className="absolute bottom-0 right-0 w-7 h-7 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors">
              <svg className="w-3.5 h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">{form.firstName} {form.lastName}</h2>
            <p className="text-sm text-gray-500">{form.city}, {form.country} · Member since 2022</p>
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-1">
                <span className="text-amber-400 text-sm">★</span>
                <span className="text-sm font-semibold text-gray-700">4.9</span>
                <span className="text-xs text-gray-400">guest rating</span>
              </div>
              <div className="text-xs text-gray-400">·</div>
              <span className="text-xs text-gray-500">6 trips completed</span>
            </div>
          </div>
          <button
            onClick={() => setEditing(!editing)}
            className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all ${
              editing ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-rose-500 text-white hover:bg-rose-600'
            }`}
          >
            {editing ? 'Cancel' : 'Edit Profile'}
          </button>
        </div>
      </div>

      {/* Profile Form */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
        <h3 className="text-base font-bold text-gray-900 mb-5">Personal Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: 'First Name', field: 'firstName' },
            { label: 'Last Name', field: 'lastName' },
            { label: 'Email Address', field: 'email' },
            { label: 'Phone Number', field: 'phone' },
            { label: 'Country', field: 'country' },
            { label: 'City', field: 'city' },
          ].map(({ label, field }) => (
            <div key={field}>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">{label}</label>
              {editing ? (
                <input
                  type="text"
                  value={form[field as keyof typeof form]}
                  onChange={(e) => handleChange(field, e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent"
                />
              ) : (
                <p className="text-sm text-gray-900 py-2.5 px-4 bg-gray-50 rounded-xl">{form[field as keyof typeof form]}</p>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4">
          <label className="block text-xs font-semibold text-gray-500 mb-1.5">Bio</label>
          {editing ? (
            <textarea
              value={form.bio}
              onChange={(e) => handleChange('bio', e.target.value)}
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent resize-none"
            />
          ) : (
            <p className="text-sm text-gray-700 py-2.5 px-4 bg-gray-50 rounded-xl leading-relaxed">{form.bio}</p>
          )}
        </div>
        {editing && (
          <button
            onClick={() => setEditing(false)}
            className="mt-5 px-6 py-2.5 bg-rose-500 text-white text-sm font-semibold rounded-xl hover:bg-rose-600 transition-colors"
          >
            Save Changes
          </button>
        )}
      </div>

      {/* Preferences */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-base font-bold text-gray-900 mb-5">Preferences</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Language</label>
            <select className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-300 bg-white">
              <option>English</option>
              <option>Swahili</option>
              <option>French</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Currency</label>
            <select className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-300 bg-white">
              <option>UGX — Ugandan Shilling</option>
              <option>USD — US Dollar</option>
              <option>KES — Kenyan Shilling</option>
            </select>
          </div>
        </div>
        <div className="mt-5 pt-5 border-t border-gray-100">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Notifications</h4>
          {[
            { label: 'Booking confirmations', checked: true },
            { label: 'Host messages', checked: true },
            { label: 'Promotional offers', checked: false },
            { label: 'Review reminders', checked: true },
          ].map((notif) => (
            <label key={notif.label} className="flex items-center justify-between py-2.5 cursor-pointer">
              <span className="text-sm text-gray-700">{notif.label}</span>
              <div className={`w-10 h-5 rounded-full transition-colors ${notif.checked ? 'bg-rose-500' : 'bg-gray-200'} relative`}>
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${notif.checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
